import { useMemo, useState } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { WorkflowCaseStatus } from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import {
  type HTMLChakraProps,
  TabsList as RawTabsList,
  TabsTrigger as RawTabsTrigger,
  TabsRoot,
  TertiaryButton,
} from '@enechain/ecloud-designsystem'
import { FaGearRegular } from '@enechain/ecloud-icons'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import {
  EMPTY_PANEL_ICON_SIZE,
  EmptyPanel,
} from '~/components/common/EmptyPanel'
import { PageBody } from '~/components/common/PageBody'
import { PageLayout } from '~/components/common/PageLayout'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { RegionLoading } from '~/components/common/RegionLoading'
import { tabTriggerFontSize } from '~/constants/tabStyle'
import { usePersistedFilters } from '~/hooks/usePersistedFilters'
import { AREA_CODE_OPTIONS } from '~/models/area'
import { now as serverNow } from '~/models/clock'
import { FilterBar } from '~/pages/DemandForecastList/components/FilterBar'
import { ForecastTable } from '~/pages/DemandForecastList/components/ForecastTable'
import { UnderDevPlaceholder } from '~/pages/DemandForecastList/components/UnderDevPlaceholder'
import { useListDemandForecasts } from '~/pages/DemandForecastList/hooks/useListDemandForecasts'
import {
  DEMAND_FORECAST_LIST_FILTERS_STORAGE_KEY,
  type DemandForecastListFilters,
  demandForecastListFiltersCodec,
} from '~/pages/DemandForecastList/models/persistedFilters'
import {
  type SelectableTabKey,
  TAB_DEFINITIONS,
  dateToProtoDate,
  getTabDefinition,
  toSelectableTabKey,
} from '~/pages/DemandForecastList/models/tabDefinitions'
import {
  type TargetDateSegmentKey,
  computeTargetDateSegment,
  targetDateForSegment,
} from '~/pages/DemandForecastList/models/targetDateSegment'
import { useGetPersonalSettingsQuery } from '~/pages/Settings/hooks/useGetPersonalSettingsQuery'
import { routePaths } from '~/routes/routePaths'

// DS `TabsComponent` は item 単位の disabled も ReactNode label も持たないため、
// 月間/年間を押せないタブ + 押せない理由の Tooltip で出すには raw slot を使う
// (AC-DFL-026、ADR: frontend-tabs-no-wrapper-closed-allowlist)。
// chakra v3 の `TabsList*Props` は本プロジェクトの型解決 (ecloud 0.14 / chakra 3.29 の
// d.ts 解決問題) で `HTMLChakraProps` が collapse し `children` / style prop が欠落する。
// v3 本来の prop シグネチャ (`HTMLChakraProps<element>`) へ cast して復元する
// (WorkflowCases/components/FilterBar.tsx と同じ)。DS 側が d.ts を修正したら外してよい。
const TabsList = RawTabsList as React.FC<HTMLChakraProps<'div'>>
const TabsTrigger = RawTabsTrigger as React.FC<
  HTMLChakraProps<'button'> & { value: string }
>

export const DemandForecastListPage = (): React.ReactElement => {
  const { t } = useTranslation()

  // アクティブタブ。 design (2026-07-15 ユーザー指示 / 7/16 design-desk 同期版)
  // の既定タブ = 単日 (唯一の一覧取得対象タブ)。 週間は開発中プレースホルダ。
  // 月間/年間は押せないタブ (AC-DFL-026) で、 選択 state には入らない。
  const [activeTab, setActiveTab] = useState<SelectableTabKey>('single')
  const tabDef = getTabDefinition(activeTab)

  // フィルタバーの 3 つ (対象日 / エリア / ステータス) は画面遷移をまたいで保持する
  // (AC-SC-031 [P0] / issue #6684「一覧 → 詳細 → 一覧 で対象日が戻る」)。
  // 直列化・復元・既定値は `models/persistedFilters.ts` の純関数が SoT。
  // 既定の対象日は単日タブの基点日 (今日) で、 ブラウザ時計ではなくサーバー由来。
  const { filters, updateFilters } =
    usePersistedFilters<DemandForecastListFilters>({
      storageKey: DEMAND_FORECAST_LIST_FILTERS_STORAGE_KEY,
      codec: demandForecastListFiltersCodec,
    })
  const targetDate = filters.targetDate
  const setTargetDate = (value: Date): void =>
    updateFilters((prev) => ({ ...prev, targetDate: value }))

  // 業務エリア (design-note demand-list-area-filter Decision #5): 一覧は read
  // のみで write しないため read-only の useGetPersonalSettingsQuery を使う
  // (usePersonalSettings の mutation 機構は不要)。
  const {
    data: personalSettingsData,
    isLoading: isBusinessAreasLoading,
    isError: isBusinessAreasError,
  } = useGetPersonalSettingsQuery()
  // エリア select の候補 = 10 エリア (沖縄含む、AREA_CODE_OPTIONS) 北→南順 ∩ 業務エリア
  // (Decision #1)。 未取得の間は空配列 (= 一覧取得は下記 gate で待機、Decision #4)。
  const businessAreas = useMemo(
    () =>
      AREA_CODE_OPTIONS.filter((area) =>
        (personalSettingsData?.settings?.businessAreas ?? []).includes(area),
      ),
    [personalSettingsData],
  )

  /**
   * 業務エリアが 1 つも無い状態 (2026-09-17 デザイナー指摘「blank UI 適用」)。
   *
   * この時は**一覧を出す意味が無い** — 一覧は業務エリアに絞って表示する仕様
   * (Decision #5) なので、エリアが空なら結果は必ず 0 件になる。それを表の
   * 「データがありません」 で見せると、**理由 (業務エリア未設定) も次の行動
   * (個人設定で追加する) も画面に出ない**。
   *
   * `design/ui-feasibility/components/empty-panel.md` の分岐に従い、面ごと
   * [[EmptyPanel]] に置き換える (「行だけ 0」 は表の空行が正で、そちらは
   * `ForecastTable` の `emptyLabel` が既に出している = 別物)。
   *
   * **取得中・取得失敗では出さない。** 未取得の間も `businessAreas` は空配列に
   * なるため、gate を付けないと読み込み中に「未設定」と断定してしまう。
   */
  const hasNoBusinessArea =
    !isBusinessAreasLoading &&
    !isBusinessAreasError &&
    businessAreas.length === 0

  // エリアフィルタ: 単一選択、既定 = 全エリア (undefined) (design-note
  // demand-list-area-filter Decision #2)。
  //
  // **業務エリア外へ落ちた保存値は「全エリア」 として扱う** (review 指摘 #6870)。
  // 個人設定から東京を外した後に同じブラウザで再訪すると、 localStorage の
  // `selectedArea` は候補 (`businessAreas`) に無いので **select は空表示になるのに
  // クエリにはそのエリアが乗り**、 業務エリア外の一覧が出てしまう。 表示と問い合わせ
  // の食い違いを作らないよう、 候補との積集合を取ってから使う。
  //
  // 個人設定の取得前 (`businessAreas` が空) は保存値を残す — ここで捨てると、
  // 取得完了までの 1 レンダーで「全エリア」 に落ちた状態が保存され、 選択が消える。
  const selectedArea =
    filters.selectedArea !== undefined &&
    businessAreas.length > 0 &&
    !businessAreas.includes(filters.selectedArea)
      ? undefined
      : filters.selectedArea
  const setSelectedArea = (value: AreaCode | undefined): void =>
    updateFilters((prev) => ({ ...prev, selectedArea: value }))

  // ステータスフィルタ: デフォルト全て (WorkflowCaseStatus)
  const selectedStatus = filters.selectedStatus
  const setSelectedStatus = (value: WorkflowCaseStatus | undefined): void =>
    updateFilters((prev) => ({ ...prev, selectedStatus: value }))

  // タブ切り替え。 対象日はタブ往復に関わらず保持する
  // (design `wireUp` は `state.tab` のみ変更し `state.date` は不変。
  // 初期表示の既定値 = 今日 は targetDate の useState 初期化子が担う)。
  // 押せないタブ・未知の値は toSelectableTabKey が捨てる (AC-DFL-026)。
  const handleTabChange = ({ value }: { value: string }): void => {
    const tabKey = toSelectableTabKey(value)
    if (tabKey !== undefined) setActiveTab(tabKey)
  }

  // 対象日 radio (当日/翌日/翌々日/翌々日以降、design 2026-07-15 ユーザー指示):
  // クリックされた区分から対象日を設定する。date picker / `<` `>` とは
  // targetDateSegment (下記) 経由で逆方向にも連動する。
  const handleTargetDateSegmentChange = (
    segment: TargetDateSegmentKey,
  ): void => {
    setTargetDate(targetDateForSegment(segment, serverNow()))
  }

  // 現在の対象日がどの区分に該当するか (date picker / `<` `>` → radio の同期)。
  // 過去日等どの区分にも該当しない場合は undefined (= 全 radio 非 active)。
  const targetDateSegment = computeTargetDateSegment(targetDate, serverNow())

  // ステータスフィルタのコールバック。
  const handleStatusChange = (status: WorkflowCaseStatus | undefined): void => {
    setSelectedStatus(status)
  }

  // BFF へ送る areaCodes (design-note demand-list-area-filter Decision #3):
  // 「全エリア」(selectedArea === undefined) は業務エリア配列をそのまま送る
  // (空配列を送ると BFF は絞り込みなし = backend 全件を返し、業務エリア外が
  // 混入するため)。 単一エリア選択時は当該 1 エリアを送る。
  const areaCodesForQuery = useMemo(
    () => (selectedArea === undefined ? businessAreas : [selectedArea]),
    [selectedArea, businessAreas],
  )

  // sections: 単日タブの sections をそのまま送信 (新しい SINGLE_DAY と旧 daily 値)。
  // プレースホルダタブでは一覧取得を行わない (下記 useListDemandForecasts の enabled)
  // ため sections は空配列でよい。
  const filter = useMemo(
    () => ({
      sections: tabDef.kind === 'data' ? tabDef.sections : [],
      targetDate: dateToProtoDate(targetDate),
      areaCodes: areaCodesForQuery,
      status: selectedStatus,
    }),
    [tabDef, targetDate, areaCodesForQuery, selectedStatus],
  )

  // 一覧取得は業務エリア解決後に発火する (design-note demand-list-area-filter
  // Decision #4): 未解決の間に発火すると「全エリア」既定選択が暫定で空配列
  // (=全件) を送り、業務エリア外のケースが一瞬混入する (flash) ため。
  // 業務エリア取得が error の場合も gate を開かない (= AC-DFL-004a: 空配列
  // (絞り込みなし = backend 全件) を送って業務エリア外を表示する経路を作らない)。
  // 新規のエラー UI は発明せず、一覧取得自体を発火しないことで既存の list
  // empty/error 表示 (ForecastTable の空状態、cf. ConnectError 時と同じ見え方) に
  // 橋渡しする (design-note States「個人設定 query が error の場合は既存のエラー
  // 表示経路に委ねる」)。
  const {
    data,
    isLoading: isListLoading,
    isPlaceholderData: isShowingPreviousRows,
  } = useListDemandForecasts(filter, {
    enabled:
      tabDef.kind === 'data' &&
      !isBusinessAreasLoading &&
      !isBusinessAreasError,
  })
  const isLoading = isListLoading || isBusinessAreasLoading
  const rows = data?.rows ?? []

  return (
    <PageLayout
      breadcrumbItems={[
        { label: t('page:demandForecastList.breadcrumb.demand') },
        {
          label: t('page:demandForecastList.breadcrumb.list'),
          bold: true,
        },
      ]}
    >
      <PageBody>
        {/* タブ: 単日/週間/月間/年間 の 4 タブ。 月間/年間 (kind 'unavailable') は
            出したまま押せず、 hover・focus で押せない理由を出す (AC-DFL-026、
            design `#dfl-tabs` の `ecl-disabled-wrap`)。 trigger は native disabled で
            focus を受けないので、 ButtonTooltip の包みが Tab の止まり先になる。 */}
        <TabsRoot onValueChange={handleTabChange} value={activeTab}>
          {/* paddingBottom 1px は選択タブの下線 (2px) を削らせないための実寸補正
              (WorkflowCases/components/FilterBar.tsx と同じ)。 */}
          <TabsList overflowX="auto" overflowY="hidden" paddingBottom="1px">
            {TAB_DEFINITIONS.map((tab) =>
              tab.kind === 'unavailable' ? (
                <ButtonTooltip
                  key={tab.key}
                  content={t('page:demandForecastList.tab.unavailableReason')}
                >
                  <TabsTrigger
                    fontSize={tabTriggerFontSize.token}
                    disabled
                    value={tab.key}
                  >
                    <span>{t(tab.i18nKey)}</span>
                  </TabsTrigger>
                </ButtonTooltip>
              ) : (
                <TabsTrigger
                  key={tab.key}
                  fontSize={tabTriggerFontSize.token}
                  value={tab.key}
                >
                  <span>{t(tab.i18nKey)}</span>
                </TabsTrigger>
              ),
            )}
          </TabsList>
        </TabsRoot>

        {tabDef.kind === 'placeholder' ? (
          // 週間: テーブル・フィルタ領域を隠し、同位置にプレースホルダを表示
          // (design 2026-07-15 ユーザー指示、`#dfl-under-dev`)。
          <UnderDevPlaceholder tabLabel={t(tabDef.i18nKey)} />
        ) : hasNoBusinessArea ? (
          /* 面が丸ごと空 (理由と次の行動を出す)。フィルタバーも出さない —
             エリア候補が 0 なので操作しても結果が変わらない。 */
          <EmptyPanel
            action={
              <TertiaryButton asChild>
                <RouterLink to={routePaths.settings.path}>
                  {t('page:demandForecastList.emptyPanel.toSettings')}
                </RouterLink>
              </TertiaryButton>
            }
            description={t(
              'page:demandForecastList.emptyPanel.noBusinessAreaDesc',
            )}
            icon={
              <FaGearRegular
                boxSize={EMPTY_PANEL_ICON_SIZE}
                aria-hidden="true"
              />
            }
            title={t('page:demandForecastList.emptyPanel.noBusinessAreaTitle')}
          />
        ) : (
          <>
            {/* フィルタバー */}
            <FilterBar
              businessAreas={businessAreas}
              onAreaChange={setSelectedArea}
              onStatusChange={handleStatusChange}
              onTargetDateChange={setTargetDate}
              onTargetDateSegmentChange={handleTargetDateSegmentChange}
              selectedArea={selectedArea}
              selectedStatus={selectedStatus}
              targetDate={targetDate}
              targetDateSegment={targetDateSegment}
            />

            {/* テーブル */}
            {/* 絞り込みを変えた直後は前の結果を影の下に残す (region-loading.md)。
                初回の読み込み中は表の読み込み行 (isLoading) が担う。 表の器
                (MasterDataTable) は flex:1 で縦を埋めてその中でスクロールするので、
                影の器も同じ flex の鎖に乗せる (block で挟むと表が縦に伸び切り、
                見出しの固定が効かなくなる)。 */}
            <PageScrollArea>
              <RegionLoading
                flexDirection="column"
                flex="1"
                display="flex"
                minHeight="0"
                isLoading={isShowingPreviousRows}
              >
                <ForecastTable isLoading={isLoading} rows={rows} />
              </RegionLoading>
            </PageScrollArea>
          </>
        )}
      </PageBody>
    </PageLayout>
  )
}
